'use client';

import { Footer } from '@/components/public/footer';
import { NavPreview } from '@/components/public/nav-preview';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useScrollReveal } from '@/lib/hooks/use-scroll-reveal';
import { Clock, DollarSign, Droplets, ChevronLeft, ChevronRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import { useTranslations, useLocale } from 'next-intl';
import { FaqAccordion } from '@/components/public/faq-accordion';
import { FAQ_CATEGORIES } from '@/lib/faq-content';
import { useFaqItems } from '@/lib/faq-hooks';

function highlightWordsInRo(text: string, locale: string, wordsToHighlight: string[]) {
  if (locale !== 'ro') return <>{text}</>;

  const sortedWords = [...wordsToHighlight].sort((a, b) => b.length - a.length);
  const regex = new RegExp(`(${sortedWords.join('|')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        const isHighlighted = sortedWords.some(word => word.toLowerCase() === part.toLowerCase());
        return isHighlighted ? (
          <span key={i} className="text-[#478B8B]">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

function highlightWordsInHu(text: string, locale: string, wordsToHighlight: string[]) {
  if (locale !== 'hu') return <>{text}</>;

  const sortedWords = [...wordsToHighlight].sort((a, b) => b.length - a.length);
  const regex = new RegExp(`(${sortedWords.join('|')})`, 'gi');
  const parts = text.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        const isHighlighted = sortedWords.some(word => word.toLowerCase() === part.toLowerCase());
        return isHighlighted ? (
          <span key={i} className="text-[#478B8B]">{part}</span>
        ) : (
          <span key={i}>{part}</span>
        );
      })}
    </>
  );
}

const galleryImages = [
  'gallery-0010DS2_4787-Enhanced-NR.jpg',
  'gallery-DS1_3637.jpg',
  'gallery-DS1_3643.jpg',
  'gallery-DS1_3647.jpg',
  'gallery-DS1_3670-Edit.jpg',
  'gallery-DS1_3687.jpg',
  'gallery-DS1_3698.jpg',
  'gallery-DS1_3717.jpg',
  'gallery-DS1_3722.jpg',
  'gallery-DS1_3734.jpg',
  'gallery-DS1_3737.jpg',
  'gallery-DS1_3747.jpg',
  'gallery-DS1_3803.jpg',
  'gallery-DS1_3807.jpg',
  'gallery-DS1_3847.jpg',
  'gallery-RAY04593.jpg',
  'gallery-RAY04626.jpg'
];

function FAQAccordion() {
  const category = FAQ_CATEGORIES.find((c) => c.id === 'spa')!;
  const items = useFaqItems(category);
  return <FaqAccordion items={items} accent={category.accent} />;
}

function GalleryCarousel() {
  const t = useTranslations('design.spa');
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: true,
    align: 'center',
    skipSnaps: false,
    dragFree: false
  });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  // Let the partially-visible neighbouring slides act as prev/next targets:
  // clicking a peeking image scrolls straight to it (loop mode picks the
  // shortest direction). A small pointer-travel guard keeps a drag from
  // registering as a click.
  const pointerDownPos = useRef<{ x: number; y: number } | null>(null);
  const handleSlidePointerDown = useCallback((e: React.PointerEvent) => {
    pointerDownPos.current = { x: e.clientX, y: e.clientY };
  }, []);
  const handleSlideClick = useCallback(
    (e: React.MouseEvent, index: number) => {
      const start = pointerDownPos.current;
      if (start && Math.hypot(e.clientX - start.x, e.clientY - start.y) > 10) return;
      emblaApi?.scrollTo(index);
    },
    [emblaApi]
  );

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
    setCanScrollPrev(emblaApi.canScrollPrev());
    setCanScrollNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative observe-me fade-in">
      {/* Main Carousel Container */}
      <div className="overflow-hidden rounded-3xl" ref={emblaRef}>
        <div className="flex touch-pan-y">
          {galleryImages.map((image, index) => (
            <div
              key={index}
              className="flex-[0_0_100%] min-w-0 md:flex-[0_0_85%] lg:flex-[0_0_70%] md:px-4"
            >
              <button
                type="button"
                onPointerDown={handleSlidePointerDown}
                onClick={(e) => handleSlideClick(e, index)}
                aria-label={t('gallery.goToImage', { number: index + 1 })}
                className="relative group block w-full rounded-2xl overflow-hidden shadow-2xl cursor-pointer"
              >
                <Image
                  src={`/spa-images/resized/${image}`}
                  alt={t('gallery.imageAlt', { number: index + 1 })}
                  width={1200}
                  height={800}
                  className="w-full md:h-[600px] lg:h-[700px] object-cover transition-transform duration-700 group-hover:scale-105"
                />
                {/* Animated gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/70 via-[#478B8B]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                {/* Floating ripple effect on hover */}
                <div className="absolute bottom-10 right-10 w-20 h-20 rounded-full border-2 border-white/40 opacity-0 group-hover:opacity-100 group-hover:animate-[ripple_2s_ease-out_infinite]" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Buttons */}
      <button
        onClick={scrollPrev}
        disabled={!canScrollPrev}
        className="absolute left-4 top-[58%] md:top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-xl hover:bg-[#478B8B] hover:scale-110 transition-all duration-300 flex items-center justify-center group opacity-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/90 disabled:hover:scale-100"
        aria-label={t('gallery.previous')}
      >
        <ChevronLeft className="w-7 h-7 text-gray-900 group-hover:text-white transition-colors" />
      </button>

      <button
        onClick={scrollNext}
        disabled={!canScrollNext}
        className="absolute right-4 top-[58%] md:top-1/2 -translate-y-1/2 z-10 w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm shadow-xl hover:bg-[#478B8B] hover:scale-110 transition-all duration-300 flex items-center justify-center group opacity-50 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-white/90 disabled:hover:scale-100"
        aria-label={t('gallery.next')}
      >
        <ChevronRight className="w-7 h-7 text-gray-900 group-hover:text-white transition-colors" />
      </button>

      {/* Elegant Dots Navigation */}
      <div className="flex items-center justify-center gap-3 mt-10">
        {galleryImages.map((_, index) => (
          <button
            key={index}
            onClick={() => scrollTo(index)}
            className={`transition-all duration-500 rounded-full ${
              index === selectedIndex
                ? 'w-12 h-3 bg-[#478B8B]'
                : 'w-3 h-3 bg-gray-300 hover:bg-[#478B8B]/50 hover:scale-125'
            }`}
            aria-label={t('gallery.goToImage', { number: index + 1 })}
          />
        ))}
      </div>

      {/* Image Counter */}
      <div className="text-center mt-6">
        <span className="text-lg text-gray-600 font-medium">
          {selectedIndex + 1} <span className="text-[#478B8B]">/</span> {galleryImages.length}
        </span>
      </div>
    </div>
  );
}

export default function SpaPage() {
  const t = useTranslations('design.spa');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useScrollReveal();

  return (
    <main className="min-h-screen bg-white text-gray-900">
      <style jsx global>{`
        @keyframes ripple {
          0% { transform: scale(1); opacity: 0.8; }
          100% { transform: scale(1.5); opacity: 0; }
        }
        @keyframes waveFloat {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          25% { transform: translateY(-15px) translateX(10px); }
          50% { transform: translateY(-8px) translateX(-8px); }
          75% { transform: translateY(-18px) translateX(5px); }
        }
        @keyframes breathe {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes rotateFloat {
          0%, 100% { transform: rotate(0deg) translateY(0px); }
          50% { transform: rotate(5deg) translateY(-10px); }
        }
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
        .image-breathe {
          animation: breathe 8s ease-in-out infinite;
        }
        .wave-float {
          animation: waveFloat 6s ease-in-out infinite;
        }
        .ripple-circle {
          animation: ripple 3s ease-out infinite;
        }
      `}</style>

      <NavPreview logoSrc="/design-images/logos/cellarium-spa-transparent.png" variant="spa" />

      {/* Hero Section - Full screen with logo and title */}
      <section className="relative h-screen w-full overflow-hidden">
        <div
          className="absolute inset-0 transition-transform duration-100"
          style={{ transform: `translateY(${scrollY * 0.5}px)` }}
        >
          <Image
            src="/spa-images/resized/hero-pool.jpg"
            alt={t('hero.imageAlt')}
            fill
            className="object-cover image-breathe"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/70" />
        </div>

        <div className="relative h-full flex flex-col items-center justify-center px-6 pt-56 lg:pt-16 text-center">
          <div className="observe-me slide-up w-full max-w-3xl">
            {/* Main Title */}
            <h1 className="font-poppins text-3xl sm:text-5xl md:text-7xl font-bold text-white mb-6 tracking-tight" style={{ textShadow: '2px 2px 30px rgba(0, 0, 0, 0.7)' }}>
              {t('hero.title')}
            </h1>

            {/* Decorative Line with Subtitle */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="h-[2px] w-20 bg-[#478B8B]" style={{ animation: 'pulse 3s ease-in-out infinite' }} />
              <p className="text-base md:text-lg uppercase tracking-[0.3em] text-white/90 [text-shadow:rgba(0,0,0,1)_1px_1px_3px] md:[text-shadow:rgb(0,0,0)_1.5px_1.5px_4px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
                {t('hero.subtitle')}
              </p>
              <div className="h-[2px] w-20 bg-[#478B8B]" style={{ animation: 'pulse 3s ease-in-out infinite', animationDelay: '1.5s' }} />
            </div>

            {/* Third line subtitle */}
            <p className="text-3xl md:text-4xl text-white/95 w-fit max-w-3xl mx-auto font-light mb-8 [text-shadow:rgba(0,0,0,1)_1.5px_1.5px_4px] md:[text-shadow:rgb(0,0,0)_2px_2px_6px]" style={{ backdropFilter: 'blur(1px)', borderRadius: '8px' }}>
              {t('hero.tagline')}
            </p>

          </div>
        </div>

      </section>

      {/* Welcome Section - Text content after hero */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-[#f5f3f0] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-16 items-center">
            {/* Text side */}
            <div className="observe-me slide-left">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold text-gray-900 mb-8">
                {t('welcome.titlePrefix')} <span className="text-[#478B8B]">{t('welcome.titleAccent')}</span>
              </h2>
              <div className="w-20 h-1 bg-[#478B8B] mb-8" />
              <p className="text-xl md:text-2xl text-gray-700 leading-relaxed">
                {t('welcome.description')}
              </p>
            </div>

            {/* Image side */}
            <div className="observe-me slide-right">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                <Image
                  src="/design-images/uploads/1771507167106-p_08.jpg"
                  alt={t('welcome.imageAlt')}
                  width={800}
                  height={600}
                  className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Indulge Section - Full width detail */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f5f3f0] to-white">
        <div className="grid lg:grid-cols-2 gap-0 min-h-[50vh]">
          {/* Image side */}
          <div className="relative h-[50vh] lg:h-auto overflow-hidden observe-me slide-left">
            <Image
              src="/spa-images/resized/herbal-infusion.jpg"
              alt={t('indulge.imageAlt')}
              fill
              className="object-cover hover:scale-105 transition-transform duration-700"
            />
          </div>

          {/* Text side */}
          <div className="flex items-center justify-center p-8 lg:p-16 observe-me slide-right">
            <div className="max-w-xl">
              <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6 text-gray-900">
                {t('indulge.titleLine1')}<br />
                <span className="text-[#478B8B]">{t('indulge.titleLine2')}</span>
              </h2>
              <div className="w-20 h-1 bg-[#478B8B] mb-6" />
              <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
                {t('indulge.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section - Experience with integrated content and Floor Plan */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#f5f3f0] to-white">
        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-8 lg:gap-12 max-w-7xl mx-auto px-6 lg:px-8">
          {/* Content side with text and CTA */}
          <div className="flex flex-col items-start justify-center py-8 lg:py-12 observe-me slide-left">
            <div className="max-w-2xl">
              <div className="w-20 h-1 bg-[#478B8B] mb-8" />

              <p className="text-xl lg:text-2xl text-gray-700 leading-relaxed mb-8">
                {t('experience.description')}
              </p>

              <p className="text-2xl lg:text-3xl font-poppins font-semibold text-[#478B8B] mb-12 leading-tight">
                {t('experience.cta')}
              </p>
            </div>
          </div>

          {/* Image side - Spa Circuit Plan */}
          <div className="relative flex items-center justify-center py-8 lg:py-12 observe-me slide-right">
            <Image
              src="/spa-images/resized/spa-circuit-plan.jpg"
              alt={t('experience.floorPlanAlt')}
              width={800}
              height={1000}
              className="w-full h-auto object-contain max-h-[70vh] rounded-3xl"
            />
          </div>
        </div>
      </section>

      {/* Facilities Section - Icons Grid */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-white via-[#e8f4f4]/30 to-white overflow-hidden">
        {/* Floating background decorations */}
        <div className="absolute top-20 right-10 w-32 h-32 rounded-full bg-[#478B8B]/5 blur-3xl" />
        <div className="absolute bottom-40 left-20 w-40 h-40 rounded-full bg-[#478B8B]/5 blur-3xl" />

        <div className="max-w-7xl mx-auto p-[1%] md:px-6">
          <div className="text-center mb-10 observe-me slide-up">
            <div className="mb-4">
              <span className="text-[#478B8B] font-semibold uppercase tracking-wider text-sm border-b-2 border-[#478B8B] pb-1">{t('facilities.label')}</span>
            </div>
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              <span className="text-[#478B8B]">{t('facilities.titleWord')}</span> {highlightWordsInHu(t('facilities.titleLine1'), useLocale(), ['világa'])}<br />
              <span className="text-[#478B8B]">{t('facilities.titleLine2')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#478B8B] mx-auto" />
          </div>

          {/* Facilities Grid
           * Icons extracted from the Cellarium Slow Down Spa floor plan PDF.
           * Source: ~/Desktop/daniel workspace/spa/plexid_3B_spa circuit fekvo A3.pdf
           * See public/spa-images/icons/README.md for extraction details and resolution notes.
           *
           * Additional icons available but not currently used on this page:
           *   05_stairs.png, 10_hot_tub.png, 11_tepidarium.png, 12_massage.png
           * These are already in public/spa-images/icons/ for future use.
           */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 lg:gap-8 mb-10">
            {[
              { icon: '/spa-images/icons/08_pool.png', label: t('facilities.pool'), delay: '0.1s' },
              { icon: '/spa-images/icons/04_sauna.png', label: t('facilities.sauna'), delay: '0.15s' },
              { icon: '/spa-images/icons/03_dezsa.png', label: t('facilities.jacuzzi'), delay: '0.2s' },
              { icon: '/spa-images/icons/02_steam_bath.png', label: t('facilities.steamBath'), delay: '0.25s' },
              { icon: '/spa-images/icons/09_floating.png', label: t('facilities.floating'), delay: '0.3s' },
              { icon: '/spa-images/icons/01_scrub_bar.png', label: t('facilities.scrubBar'), delay: '0.35s' },
              { icon: '/spa-images/icons/07_virillium.png', label: t('facilities.virillium'), delay: '0.4s' },
              { icon: '/spa-images/icons/06_szekler_kneipp.png', label: t('facilities.kneipp'), delay: '0.45s' },
              { icon: '/spa-images/icons/13_hay_bed.png?v=2', label: t('facilities.hayBed'), delay: '0.5s' },
            ].map((facility) => (
              <div key={facility.label} className="observe-me slide-up" style={{ animationDelay: facility.delay }}>
                <div className="group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2">
                  <div className="flex flex-col items-center text-center">
                    <div className="w-24 h-24 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-500">
                      <Image
                        src={facility.icon}
                        alt={facility.label}
                        width={80}
                        height={80}
                        className="w-20 h-20 object-contain"
                        style={{ filter: 'brightness(0) saturate(100%) invert(50%) sepia(15%) saturate(1200%) hue-rotate(130deg) brightness(88%) contrast(92%)' }}
                      />
                    </div>
                    <h3 className="font-semibold text-lg text-gray-900">{facility.label}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Closing text - Hungarian only */}
          {useLocale() === 'hu' && (
            <div className="text-center observe-me fade-in">
              <p className="text-xl md:text-2xl text-[#478B8B] font-semibold">
                {t('facilities.closingText')}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Operating Hours & Pricing - Dual Panel Section */}
      <section className="relative py-12 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto p-[1%] md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Operating Hours Panel */}
            <div className="observe-me slide-left">
              <div className="bg-gradient-to-br from-[#e8f4f4] to-white rounded-3xl p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow duration-500">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-full bg-[#478B8B] flex items-center justify-center">
                    <Clock className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-poppins text-3xl lg:text-4xl font-bold text-gray-900">
                    {t('hours.title')}
                  </h2>
                </div>

                <div className="space-y-6">
                  <div>
                    <p className="text-lg text-gray-600 mb-6">
                      {t('hours.sessionInfo')}
                    </p>
                  </div>

                  <div className="border-t border-[#478B8B]/20 pt-6">
                    <h3 className="font-semibold text-xl text-gray-900 mb-3">{t('hours.weekdays')}</h3>
                    <p className="text-lg text-gray-700">
                      16:00–18:00<br />
                      18:30–20:30
                    </p>
                  </div>

                  <div className="border-t border-[#478B8B]/20 pt-6">
                    <h3 className="font-semibold text-xl text-gray-900 mb-3">{t('hours.weekends')}</h3>
                    <p className="text-lg text-gray-700">
                      10:00–12:00<br />
                      12:30–14:30<br />
                      16:00–18:00<br />
                      18:30–20:30
                    </p>
                  </div>

                  <div className="bg-[#478B8B]/10 rounded-2xl p-5 mt-6">
                    <p className="text-base text-gray-700">
                      <strong className="text-[#478B8B]">{t('hours.noteLabel')}</strong> {t('hours.noteText')}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Panel */}
            <div className="observe-me slide-right">
              <div className="bg-gradient-to-br from-[#faf8f6] to-white rounded-3xl p-8 lg:p-12 shadow-xl hover:shadow-2xl transition-shadow duration-500">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-14 h-14 rounded-full bg-[#478B8B] flex items-center justify-center">
                    <DollarSign className="w-7 h-7 text-white" />
                  </div>
                  <h2 className="font-poppins text-3xl lg:text-4xl font-bold text-gray-900">
                    {t('pricing.title')}
                  </h2>
                </div>

                <div className="space-y-4">
                  <div className="flex justify-between items-center py-4 border-b border-gray-200">
                    <span className="text-lg font-medium text-gray-900">{t('pricing.castleGuests')}</span>
                    <span className="text-2xl font-bold text-[#478B8B]">{t('pricing.castleGuestsPrice')}</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-gray-200">
                    <span className="text-lg font-medium text-gray-900">{t('pricing.occasionalGuests')}</span>
                    <span className="text-2xl font-bold text-[#478B8B]">{t('pricing.occasionalGuestsPrice')}</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-gray-200">
                    <span className="text-lg font-medium text-gray-900">{t('pricing.childrenUnder3')}</span>
                    <span className="text-2xl font-bold text-emerald-600">{t('pricing.free')}</span>
                  </div>
                  <div className="flex justify-between items-center py-4 border-b border-gray-200">
                    <span className="text-lg font-medium text-gray-900">{t('pricing.children3to12')}</span>
                    <span className="text-2xl font-bold text-[#478B8B]">{t('pricing.halfPrice')}</span>
                  </div>
                  <div className="flex justify-between items-center py-4">
                    <span className="text-lg font-medium text-gray-900">{t('pricing.over12')}</span>
                    <span className="text-xl text-gray-600">{t('pricing.adultsEntry')}</span>
                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monthly Pass Section - Coming Soon */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-white to-[#f5f3f0] overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-[#478B8B]/5 blur-3xl" />
        <div className="absolute bottom-20 right-10 w-40 h-40 rounded-full bg-[#478B8B]/5 blur-3xl" />

        <div className="max-w-7xl mx-auto px-6">
          {/* Coming Soon Badge */}
          <div className="text-center mb-8 observe-me fade-in">
            <span className="inline-block px-6 py-2 bg-[#478B8B]/10 border-2 border-[#478B8B]/30 rounded-full text-[#478B8B] font-semibold text-sm uppercase tracking-wider">
              {t('monthlyPass.comingSoon')}
            </span>
          </div>

          <div className="text-center mb-10 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              {t('monthlyPass.titleLine1')} <span className="text-[#478B8B]">{t('monthlyPass.titleLine2')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#478B8B] mx-auto mb-6" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('monthlyPass.subtitle')}
            </p>
          </div>

          {/* Pass Options Grid */}
          <div className="grid md:grid-cols-2 gap-8 lg:gap-12 max-w-5xl mx-auto">
            {/* 10 Entries Pass */}
            <div className="observe-me slide-left">
              <div className="bg-white rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-transparent hover:border-[#478B8B]/20">
                <div className="text-center mb-8">
                  <h3 className="font-poppins text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                    {t('monthlyPass.tenEntries')}
                  </h3>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-5xl font-bold text-[#478B8B]">500</span>
                    <span className="text-2xl text-gray-400">RON</span>
                  </div>
                  <p className="text-gray-600">{t('monthlyPass.validOneMonth')}</p>
                </div>

                {/* Visual representation - 10 drops in 2 rows */}
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {[...Array(10)].map((_, i) => (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-[#478B8B]/20 to-[#478B8B]/40 flex items-center justify-center"
                    >
                      <Droplets className="w-6 h-6 text-[#478B8B]" />
                    </div>
                  ))}
                </div>

              </div>
            </div>

            {/* 15 Entries Pass - Featured */}
            <div className="observe-me slide-right">
              <div className="bg-gradient-to-br from-[#478B8B]/10 to-white rounded-3xl p-8 lg:p-10 shadow-xl hover:shadow-2xl transition-all duration-500 border-2 border-[#478B8B]/30 relative">
                {/* Best Value Badge */}
                <div className="absolute -top-4 right-8">
                  <span className="inline-block px-4 py-2 bg-[#478B8B] text-white font-semibold text-sm rounded-full shadow-lg">
                    {t('monthlyPass.bestValue')}
                  </span>
                </div>

                <div className="text-center mb-8">
                  <h3 className="font-poppins text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                    {t('monthlyPass.fifteenEntries')}
                  </h3>
                  <div className="flex items-baseline justify-center gap-2 mb-4">
                    <span className="text-5xl font-bold text-[#478B8B]">750</span>
                    <span className="text-2xl text-gray-400">RON</span>
                  </div>
                  <p className="text-gray-600">{t('monthlyPass.validTwoMonths')}</p>
                </div>

                {/* Visual representation - 15 drops in 3 rows */}
                <div className="flex flex-wrap justify-center gap-3 mb-6">
                  {[...Array(15)].map((_, i) => (
                    <div
                      key={i}
                      className="w-12 h-12 rounded-full bg-gradient-to-br from-[#478B8B]/20 to-[#478B8B]/40 flex items-center justify-center"
                    >
                      <Droplets className="w-6 h-6 text-[#478B8B]" />
                    </div>
                  ))}
                </div>

              </div>
            </div>
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center observe-me fade-in">
            <p className="text-gray-500 italic">
              {t('monthlyPass.disclaimer')}
            </p>
          </div>
        </div>
      </section>

      {/* Sauna Aufguss Ceremony Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-[#f5f3f0] to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center observe-me slide-left">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
              <Image
                src="/design-images/uploads/1781184625358-6q9rn3-ds1_3901.jpg"
                alt={t('aufguss.imageAlt')}
                width={800}
                height={600}
                className="w-full h-[400px] lg:h-[500px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/40 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
            </div>

            <div className="observe-me slide-right">
              <h2 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                {highlightWordsInHu(t('aufguss.titleLine1'), useLocale(), ['Szauna'])}<br />
                {useLocale() !== 'hu' && <span className="text-[#478B8B]">{t('aufguss.titleLine2')}</span>}
              </h2>
              <div className="w-16 h-1 bg-[#478B8B] mb-6" />
              <div className="text-gray-600 leading-relaxed">
                {(() => {
                  const lines = t('aufguss.description').split('\n').filter(line => line.trim());
                  const reservationStartIndex = lines.length - 3;

                  return lines.map((line, index) => {
                    const isReservationDetail = index >= reservationStartIndex;
                    return (
                      <p
                        key={index}
                        className={isReservationDetail ? 'text-sm italic text-gray-600 mb-1' : 'text-lg mb-4'}
                      >
                        {line}
                      </p>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Massage Treatments Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-white to-[#f5f3f0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-10 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              {highlightWordsInHu(t('massage.titleLine1'), useLocale(), ['A gyógyító érintés'])}<br />
              <span className="text-[#478B8B]">{t('massage.titleLine2')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#478B8B] mx-auto mb-6" />
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {t('massage.subtitle')}
            </p>
          </div>

          <div className="space-y-12 lg:space-y-16">
            {/* Warm Healing Massage */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center observe-me slide-left">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                <Image
                  src="/spa-images/resized/massage-warm-healing.jpg"
                  alt={t('massage.warm.imageAlt')}
                  width={800}
                  height={500}
                  className="w-full h-[77vw] md:h-[500px] object-cover object-right group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              <div>
                <div className="mb-2">
                  <span className="text-[#478B8B] font-medium text-sm">{t('massage.warm.duration')}</span>
                </div>
                <h3 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {highlightWordsInRo(t('massage.warm.titleLine1'), useLocale(), ['Remedii Locale'])}<br />
                  <span className="text-[#478B8B]">{t('massage.warm.titleLine2')}</span>
                </h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-2xl md:text-3xl font-bold text-[#478B8B]">240</span>
                  <span className="text-lg text-gray-600">RON</span>
                </div>
                <div className="w-16 h-1 bg-[#478B8B] mb-6" />
                <p className="text-lg text-gray-600 leading-relaxed">
                  {t('massage.warm.description')}
                </p>
              </div>
            </div>

            {/* Slow Down Aromatherapy Massage */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center observe-me slide-right">
              <div className="order-2 lg:order-1">
                <div className="mb-2">
                  <span className="text-[#478B8B] font-medium text-sm">{t('massage.slowDown.duration')}</span>
                </div>
                <h3 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {highlightWordsInRo(t('massage.slowDown.titleLine1'), useLocale(), ['Aromaterapeutic'])}<br />
                  <span className="text-[#478B8B]">{t('massage.slowDown.titleLine2')}</span>
                </h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-2xl md:text-3xl font-bold text-[#478B8B]">190</span>
                  <span className="text-lg text-gray-600">RON</span>
                </div>
                <div className="w-16 h-1 bg-[#478B8B] mb-6" />
                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  {t('massage.slowDown.description')}
                </p>
                <p className="text-base text-gray-500 italic">
                  {t('massage.preBook')}
                </p>
              </div>

              <div className="relative rounded-3xl overflow-hidden shadow-2xl group order-1 lg:order-2">
                <Image
                  src="/spa-images/resized/massage-slow-down.jpg"
                  alt={t('massage.slowDown.imageAlt')}
                  width={800}
                  height={500}
                  className="w-full h-[77vw] md:h-[500px] object-cover object-right group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>

            {/* Reinvigorating Partial Massage */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center observe-me slide-left">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl group">
                <Image
                  src="/spa-images/resized/massage-reinvigorating.jpg"
                  alt={t('massage.reinvigorating.imageAlt')}
                  width={800}
                  height={500}
                  className="w-full h-[77vw] md:h-[500px] object-cover object-right group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#478B8B]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>

              <div>
                <div className="mb-2">
                  <span className="text-[#478B8B] font-medium text-sm">{t('massage.reinvigorating.duration')}</span>
                </div>
                <h3 className="font-poppins text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                  {highlightWordsInHu(t('massage.reinvigorating.titleLine1'), useLocale(), ['Frissítő részleges'])}<br />
                  <span className="text-[#478B8B]">{t('massage.reinvigorating.titleLine2')}</span>
                </h3>
                <div className="flex items-baseline gap-2 mb-6">
                  <span className="text-2xl md:text-3xl font-bold text-[#478B8B]">139</span>
                  <span className="text-lg text-gray-600">RON</span>
                </div>
                <div className="w-16 h-1 bg-[#478B8B] mb-6" />
                <p className="text-lg text-gray-600 leading-relaxed mb-4">
                  {t('massage.reinvigorating.description')}
                </p>
                <p className="text-base text-gray-500 italic">
                  {t('massage.preBook')}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-1/4 right-10 w-4 h-4 bg-[#478B8B] rounded-full wave-float opacity-50" />
        <div className="absolute bottom-1/3 left-16 w-3 h-3 bg-[#478B8B] rounded-full wave-float opacity-60" style={{ animationDelay: '2s' }} />
      </section>

      {/* Gallery Section - Interactive Carousel */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-white via-[#f5f3f0] to-white overflow-hidden">
        <div className="max-w-7xl mx-auto p-[1%] md:px-6">
          <div className="text-center mb-10 observe-me slide-up">
            <h2 className="font-poppins text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6">
              <span className="text-[#478B8B]">{t('gallery.title')}</span>
            </h2>
            <div className="w-20 h-1 bg-[#478B8B] mx-auto" />
          </div>

          <GalleryCarousel />
        </div>

        {/* Floating decorative elements */}
        <div className="absolute top-1/4 right-10 w-4 h-4 bg-[#478B8B] rounded-full wave-float opacity-50" />
        <div className="absolute bottom-1/3 left-16 w-3 h-3 bg-[#478B8B] rounded-full wave-float opacity-60" style={{ animationDelay: '2s' }} />
      </section>

      {/* FAQ Section */}
      <section className="relative py-12 lg:py-20 bg-gradient-to-b from-white to-[#e8f4f4]/30">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-10 observe-me slide-up">
            <h2 className="font-poppins text-[clamp(2.25rem,3.5vw,4rem)] font-bold mb-6">
              <span className="text-[#478B8B]">{t('faq.title')}</span>
              <br />
              <span className="text-gray-800 text-3xl md:text-[clamp(1.75rem,2.8vw,3rem)] font-normal mt-3 block">
                {t('faq.subtitle')}
              </span>
            </h2>
            <div className="w-16 h-1 bg-[#478B8B] mx-auto" />
          </div>

          <FAQAccordion />
        </div>
      </section>

      <Footer variant="spa" />
    </main>
  );
}
